"use client";

import Image from "next/image";
import Link from "next/link";
import React from "react";

export interface WOurDoctorsSectionDoctor {
  imageUrl?: string;
  imageAlt?: string;
  name?: string;
  qualification?: string;
  position?: string;
  linkEnabled?: boolean;
  linkUrl?: string;
}

export interface WOurDoctorsSectionProps {
  heading?: string;
  description?: string;
  doctors?: WOurDoctorsSectionDoctor[];
}

export default function WOurDoctorsSection({
  heading,
  description,
  doctors = [],
}: WOurDoctorsSectionProps) {
  const displayHeading =
    heading != null && String(heading).trim() !== "" ? String(heading).trim() : "";
  const displayDescription =
    description != null && String(description).trim() !== ""
      ? String(description).trim()
      : "";
  const validDoctors = doctors.filter(
    (d) => d?.name != null && String(d.name).trim() !== ""
  );
  const primaryDoctors = validDoctors.slice(0, 2);

  if (primaryDoctors.length === 0) return null;

  return (
    <section
      className="mx-auto w-full max-w-6xl py-8 md:py-10"
      aria-label={displayHeading || "Our doctors"}
    >
      <div className="mx-auto w-full px-4 md:px-6">
        <div className="flex flex-col gap-8 md:flex-row md:items-center">
          <div className="md:w-5/12 lg:w-4/12">
            {displayHeading !== "" && (
              <h2 className="text-2xl font-extrabold tracking-tight text-violet-950 md:text-3xl">
                {displayHeading}
              </h2>
            )}
            {displayDescription !== "" && (
              <p className="mt-3 text-gray-600 md:text-lg">
                {displayDescription}
              </p>
            )}
          </div>

          <div className="mt-6 md:mt-0 md:w-7/12 lg:w-8/12">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-stretch">
              {primaryDoctors.map((doctor, index) => (
                <article
                  key={index}
                  className="flex flex-1 flex-col items-center rounded-xl p-5 text-center"
                >
                  {doctor.imageUrl && (
                    <div className="mb-4 flex h-52 w-52 items-center justify-center rounded-full bg-white shadow-lg ring ring-white">
                      <Image
                        src={doctor.imageUrl}
                        alt={doctor.imageAlt || doctor.name || "Doctor"}
                        width={176}
                        height={176}
                        className="h-44 w-44 rounded-full object-cover"
                        sizes="176px"
                        unoptimized={
                          doctor.imageUrl.startsWith("http") &&
                          !doctor.imageUrl.includes("trichos")
                        }
                      />
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-gray-900 md:text-xl">
                    {doctor.name}
                  </h3>
                  {doctor.position != null &&
                    String(doctor.position).trim() !== "" && (
                      <p className="mt-1 text-sm text-gray-600">
                        {String(doctor.position).trim()}
                      </p>
                    )}
                  {doctor.qualification != null &&
                    String(doctor.qualification).trim() !== "" && (
                      <p className="mt-0.5 text-sm text-gray-500">
                        {String(doctor.qualification).trim()}
                      </p>
                    )}
                  {doctor.linkEnabled === true &&
                    doctor.linkUrl != null &&
                    String(doctor.linkUrl).trim() !== "" && (
                      <Link
                        href={String(doctor.linkUrl).trim()}
                        className="mt-3 inline-flex items-center text-sm font-semibold text-violet-700 underline-offset-2 hover:underline"
                      >
                        View full profile
                      </Link>
                    )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
